import { message } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getMyPosts, getPosts } from '../../services/postApi';
import EmptyFeed from './EmptyStates/EmptyFeed';
import ErrorState from './EmptyStates/ErrorState';
import './FeedContainer.css';
import FeedPost from './FeedPost/FeedPost';
import FeedSkeleton from './LoadingStates/FeedSkeleton';

// Debounce utility for scroll performance
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const FeedContainer = ({ activeTab, onMediaClick, postType, excludePostId = null, compact = false }) => {
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const sentinelRef = useRef();
  const loadingRef = useRef(false);

  const BATCH_SIZE = 2; // Facebook-style small batches
  const MAX_POSTS_IN_DOM = 50;
  const POSTS_TO_REMOVE = 20;

  const loadPosts = useCallback(async (cursor = null, reset = false) => {
    if (loadingRef.current) return;

    loadingRef.current = true;

    if (reset) {
      setInitialLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        limit: BATCH_SIZE,
        ...(postType && { post_type: postType })
      };

      // Add cursor if provided
      if (cursor) {
        params.cursor = cursor;
      }

      // Use different API endpoints based on active tab
      const response = activeTab === 'my-posts'
        ? await getMyPosts(params)
        : await getPosts(params);

      const newPosts = response.data.posts;

      if (reset) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }

      // Extract cursor from last post (simplified approach)
      const extractedCursor = newPosts.length > 0 ? newPosts[newPosts.length - 1].id : null;
      setNextCursor(extractedCursor);

      // Simple end detection: if we got fewer posts than requested, we're at the end
      setHasMore(newPosts.length === BATCH_SIZE);
      setHasLoaded(true);

    } catch (err) {
      console.error('Error loading posts:', err);
      setError(err);
      message.error('Failed to load posts');
    } finally {
      setInitialLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [activeTab, postType]);

  const loadMorePosts = useCallback(() => {
    if (!loadingRef.current && hasMore && !error && hasLoaded && nextCursor) {
      loadPosts(nextCursor, false);
    }
  }, [hasMore, error, hasLoaded, nextCursor, loadPosts]);

  // Optimized debounce for faster response (like Facebook)
  const debouncedLoadMore = useCallback(
    debounce(loadMorePosts, 50), // Reduced from 100ms to 50ms
    [loadMorePosts]
  );

  // Memory management - cleanup old posts
  useEffect(() => {
    if (posts.length > MAX_POSTS_IN_DOM) {
      const postsToKeep = posts.slice(POSTS_TO_REMOVE);
      setPosts(postsToKeep);

      // Estimate removed height and adjust scroll position
      const estimatedPostHeight = 400;
      const removedHeight = POSTS_TO_REMOVE * estimatedPostHeight;

      requestAnimationFrame(() => {
        window.scrollBy(0, -removedHeight);
      });
    }
  }, [posts.length]);

  // Enhanced Intersection Observer for Facebook-style infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && hasMore && !loadingRef.current && nextCursor) {
            requestAnimationFrame(() => {
              debouncedLoadMore();
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '300px', // Aggressive prefetching like Facebook
        passive: true
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, debouncedLoadMore, nextCursor]);

  // Load initial posts when component mounts or tab changes
  useEffect(() => {
    setHasLoaded(false);
    setPosts([]);
    setNextCursor(null);
    setHasMore(true);
    setError(null);
    loadPosts(null, true);
  }, [activeTab, loadPosts]);

  const handleMediaClick = useCallback((post, mediaIndex) => {
    if (post && onMediaClick) {
      onMediaClick(post, mediaIndex);
    }
  }, [onMediaClick]);

  const handleRetry = useCallback(() => {
    setError(null);
    setHasLoaded(false);
    setPosts([]);
    setNextCursor(null);
    setHasMore(true);
    loadPosts(null, true);
  }, [loadPosts]);

  // Show initial loading state only if never loaded
  if (!hasLoaded && initialLoading) {
    return <FeedSkeleton count={3} isDarkMode={isDarkMode} />;
  }

  if (error && posts.length === 0) {
    return <ErrorState onRetry={handleRetry} isDarkMode={isDarkMode} />;
  }

  if (posts.length === 0 && hasLoaded && !initialLoading) {
    return (
      <EmptyFeed
        activeTab={activeTab}
        isDarkMode={isDarkMode}
      />
    );
  }

  return (
    <div className={`feed-container ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="feed-stream">
        {posts.filter(post => !excludePostId || post.id !== excludePostId).map((post, index) => (
          <FeedPost
            key={`${post.id}-${index}`}
            post={post}
            onMediaClick={handleMediaClick}
            isDarkMode={isDarkMode}
            compact={compact}
          />
        ))}

        {/* Loading more posts skeleton */}
        {loadingMore && (
          <div className="loading-more-container">
            <FeedSkeleton count={1} isDarkMode={isDarkMode} />
          </div>
        )}

        {/* Error state for failed loads */}
        {error && posts.length > 0 && (
          <div className="feed-error">
            <p>Failed to load more posts</p>
            <button onClick={handleRetry} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {/* Intersection observer sentinel - only show if we have more posts */}
        {hasMore && hasLoaded && nextCursor && (
          <div
            ref={sentinelRef}
            className="feed-sentinel"
          >
            {loadingMore && (
              <div className="sentinel-loading">
                Loading more posts...
              </div>
            )}
          </div>
        )}

        {/* End of feed indicator */}
        {!hasMore && posts.length > 0 && hasLoaded && (
          <div className="feed-end">
            <p>You've reached the end of the feed</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedContainer;
